import * as React from 'react';

import moment from 'moment';

import {getTimezoneAbbreviation} from 'nutshell-core/date-time/get-timezone-abbreviation';

// @ts-expect-error
import {LocationEnum, Popover} from '../popover/popover';
import {useRefObserver} from '../utils/hooks/use-ref-observer';

import {Timestamp} from './timestamp';

const FORMATS = {
    time: 'h:mm A',
    timeWithDate: 'h:mm A [on] MMM D',
    timeWithDateYear: 'h:mm A [on] MMM D, YYYY',
    dayWithTime: 'dddd[,] h:mm A',
    shortMonthDay: 'MMM DD',
    shortMonthDayWithTime: 'MMM Do, h:mm a',
    shortMonthDayWithTimeCapitalized: 'MMM Do, h:mm A',
    shortMonthDayWithYearWithTimeCapitalized: 'MMM Do, YYYY, h:mm A',
    longDateWithDay: 'dddd MMM Do, YYYY',
    monthDay: 'MMM Do',
    monthDayYear: 'MMM Do, YYYY',
    longMonthDay: 'MMMM Do',
    longMonthDayWithYear: 'MMMM Do, YYYY',
    longMonthDayYearWithTime: 'MMMM Do, YYYY h:mm A',
    dateWithSlashes: 'L',
    simpleLongMonthDayYear: 'MMMM D, YYYY',
    longDateWithTime: 'LLLL',
    longDayMonth: 'dddd, MMMM Do',
    longDayMonthWithAtTime: 'dddd, MMMM Do [at] h:mm A',
    longDayMonthYearWithAtTime: 'dddd, MMMM Do, YYYY, [at] h:mm A',
    shortDayMonthWithAtTime: 'MMMM Do [at] h:mm A',
    shortDayMonthYearWithAtTime: 'MMM Do, YYYY [at] h:mm A',
    shortDayLongMonthYearWithAtTime: 'MMMM Do, YYYY [at] h:mm A',
} as const;

export type Format = (typeof FORMATS)[keyof typeof FORMATS];

type PopoverConfig = {
    location?: LocationEnum;
    bodyColor?: 'light' | 'dark' | 'error';
    isNubDark?: boolean;
};

type Props = {
    datetime: number;
    format: Format;
    addTimezoneAbbreviation?: boolean;
    className?: string;
    detailedFormat?: Format;
    showDetailedPopover?: boolean;
    popoverConfig?: PopoverConfig;
    isInline?: boolean;
};

const DEFAULT_POPOVER_CONFIG: PopoverConfig = {
    location: 'top',
    bodyColor: 'dark',
    isNubDark: true,
};

FormattedTimestamp.formats = FORMATS;

export function FormattedTimestamp(props: Props) {
    const {datetime, format} = props;
    const timestampRef = React.useRef(null);
    const {isHovered} = useRefObserver(timestampRef, ['isHovered']);

    const datetimeValue = moment.unix(datetime);

    let timestamp = datetimeValue.isValid() ? datetimeValue.format(format) : '';

    if (props.addTimezoneAbbreviation) {
        timestamp = addTimezoneAbbreviation(timestamp, datetime);
    }

    const detailedFormat = props.detailedFormat || FORMATS.longMonthDayYearWithTime;
    const detailedTimestamp = datetimeValue.isValid() ? datetimeValue.format(detailedFormat) : '';

    const timestampElement = <Timestamp {...props} timestamp={timestamp} />;

    if (props.showDetailedPopover) {
        const popoverConfig = {...DEFAULT_POPOVER_CONFIG, ...props.popoverConfig} as const;

        return (
            <div ref={timestampRef} className='inline-block'>
                {timestampElement}
                {isHovered && timestampRef.current && (
                    <Popover
                        anchor={timestampRef.current}
                        location={popoverConfig.location}
                        isNubDark={popoverConfig.isNubDark}
                        bodyColor={popoverConfig.bodyColor}
                    >
                        <div className='pad-8'>{detailedTimestamp}</div>
                    </Popover>
                )}
            </div>
        );
    }

    return props.isInline ? (
        <div className='inline-block'>{timestampElement}</div>
    ) : (
        timestampElement
    );
}

const addTimezoneAbbreviation = (timestamp: string, datetime: number) => {
    const timezone = getTimezoneAbbreviation(datetime);

    if (timezone) {
        return `${timestamp} ${timezone}`;
    } else {
        return timestamp;
    }
};
